// Predefined region catalog. Each region either filters by bbox (used
// for islands) or by province name match (used for mainland cities).
// The "view" is the {lat, lng, zoom} the map should fly to when the
// region is picked.
//
// Keys here are what `?region=<key>` in the URL hash binds to.

import type { Bbox } from "./types";

export interface Region {
  key: string;
  /** Either a translation key (for "all" / "canarias") or a literal label. */
  label: string;
  /** If set, filter by inBbox(station, bbox). Used for islands. */
  bbox: Bbox | null;
  /** If set, filter by stations whose `province` is in this list. */
  provinces: string[] | null;
  /** Map view to set when this region is selected. */
  view: { lat: number; lng: number; zoom: number } | null;
}

/** Center of Spain for the default "all" view. */
export const DEFAULT_VIEW = { lat: 40.4, lng: -3.7, zoom: 6 };

export const REGIONS: Region[] = [
  {
    key: "all",
    label: "region_all",
    bbox: null,
    provinces: null,
    view: { ...DEFAULT_VIEW },
  },

  // Canary Islands — umbrella region covering the whole archipelago.
  // This is the key that ?region=canarias binds to from the embed URL,
  // used by wyspykanaryjskie.info so the whole archipelago is framed
  // on first render instead of mainland Spain.
  {
    key: "canarias",
    label: "Islas Canarias",
    bbox: { west: -18.2, south: 27.6, east: -13.4, north: 29.4 },
    provinces: null,
    view: { lat: 28.3, lng: -16.0, zoom: 8 },
  },

  // Individual Canary Islands (for the in-app filter dropdown).
  {
    key: "tenerife",
    label: "Tenerife",
    bbox: { west: -16.95, south: 28.0, east: -16.1, north: 28.6 },
    provinces: null,
    view: { lat: 28.3, lng: -16.55, zoom: 10 },
  },
  {
    key: "gran_canaria",
    label: "Gran Canaria",
    bbox: { west: -15.85, south: 27.7, east: -15.35, north: 28.2 },
    provinces: null,
    view: { lat: 27.95, lng: -15.6, zoom: 10 },
  },
  {
    key: "lanzarote",
    label: "Lanzarote",
    bbox: { west: -13.95, south: 28.85, east: -13.4, north: 29.25 },
    provinces: null,
    view: { lat: 29.05, lng: -13.65, zoom: 10 },
  },
  {
    key: "fuerteventura",
    label: "Fuerteventura",
    bbox: { west: -14.55, south: 28.0, east: -13.8, north: 28.75 },
    provinces: null,
    view: { lat: 28.4, lng: -14.05, zoom: 10 },
  },
  {
    key: "la_palma",
    label: "La Palma",
    bbox: { west: -18.0, south: 28.4, east: -17.7, north: 28.9 },
    provinces: null,
    view: { lat: 28.65, lng: -17.85, zoom: 11 },
  },
  {
    key: "la_gomera",
    label: "La Gomera",
    bbox: { west: -17.4, south: 28.0, east: -17.0, north: 28.25 },
    provinces: null,
    view: { lat: 28.1, lng: -17.2, zoom: 11 },
  },
  {
    key: "el_hierro",
    label: "El Hierro",
    bbox: { west: -18.2, south: 27.6, east: -17.85, north: 27.85 },
    provinces: null,
    view: { lat: 27.75, lng: -18.0, zoom: 11 },
  },

  // Mainland Spain — popular cities.
  {
    key: "madrid",
    label: "Madrid",
    bbox: null,
    provinces: ["MADRID"],
    view: { lat: 40.42, lng: -3.7, zoom: 11 },
  },
  {
    key: "barcelona",
    label: "Barcelona",
    bbox: null,
    provinces: ["BARCELONA"],
    view: { lat: 41.38, lng: 2.17, zoom: 11 },
  },
  {
    key: "valencia",
    label: "Valencia",
    bbox: null,
    provinces: ["VALENCIA"],
    view: { lat: 39.47, lng: -0.38, zoom: 11 },
  },
  {
    key: "malaga",
    label: "Málaga",
    bbox: null,
    // MITERD sends "MÁLAGA" with the accent — matching without the accent
    // would silently return zero stations.
    provinces: ["MÁLAGA"],
    view: { lat: 36.72, lng: -4.42, zoom: 11 },
  },
];

export function findRegion(key: string): Region | undefined {
  return REGIONS.find((r) => r.key === key);
}
